/**
 * Subscription plans (PLAN.md pivot: monthly SaaS with an included order volume,
 * no per-order fees).
 *
 * Prices deliberately live in Stripe, not in code: billing is feature-gated
 * (`features.stripe`), and nothing here charges anyone. What the app uses this
 * catalog for is the honest usage meter shown on the dashboard home: how many
 * orders this calendar month against what the restaurant's plan includes.
 *
 * `includedOrdersPerMonth: null` = unlimited. An unknown plan string resolves to
 * null (shown as "unknown plan") rather than silently behaving like a real tier.
 */
export interface PlanInfo {
  id: string;
  label: string;
  /** null = unlimited. */
  includedOrdersPerMonth: number | null;
  /** One-line description for the dashboard card. */
  blurb: string;
}

export const PLANS: readonly PlanInfo[] = [
  {
    id: 'free',
    label: 'Free trial',
    includedOrdersPerMonth: 25,
    blurb: 'Website, SEO and online ordering while you try adop out.',
  },
  {
    id: 'starter',
    label: 'Starter',
    includedOrdersPerMonth: 150,
    blurb: 'For single-location restaurants getting started with direct ordering.',
  },
  {
    id: 'growth',
    label: 'Growth',
    includedOrdersPerMonth: 500,
    blurb: 'For busy restaurants running direct orders and table QR ordering.',
  },
  {
    id: 'pro',
    label: 'Pro',
    includedOrdersPerMonth: null,
    blurb: 'Unlimited orders, multiple locations and priority support.',
  },
] as const;

export const PLAN_IDS: readonly string[] = PLANS.map((plan) => plan.id);

/** Unknown plans return null so the UI fails loudly instead of guessing. */
export function planInfo(plan: string | null | undefined): PlanInfo | null {
  if (!plan) return null;
  return PLANS.find((candidate) => candidate.id === plan) ?? null;
}
